/**
 * FrameworkSidebar Component (DC-1059)
 * ============================================================================
 * Collapsible sidebar for navigating frameworks (regulatory) or topics (literature).
 * Shows document/citation counts per item.
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React from 'react';
import type { RegulatoryFrameworkInfo, LiteratureTopic } from '../../types/evidence';
import './framework-sidebar.css';

// ============================================================================
// Types
// ============================================================================

export interface FrameworkSidebarProps {
  /** Current active tab */
  activeTab: 'regulatory' | 'literature';
  /** Selected framework (regulatory tab) */
  selectedFramework: string | null;
  /** Framework selection handler */
  onSelectFramework: (framework: string | null) => void;
  /** Available frameworks */
  frameworks: RegulatoryFrameworkInfo[];
  /** Literature topics (literature tab) */
  literatureTopics?: LiteratureTopic[];
  /** Selected topic (literature tab) */
  selectedTopic?: string | null;
  /** Topic selection handler */
  onSelectTopic?: (topicId: string | null) => void;
}

// ============================================================================
// Component
// ============================================================================

export const FrameworkSidebar: React.FC<FrameworkSidebarProps> = ({
  activeTab,
  selectedFramework,
  onSelectFramework,
  frameworks,
  literatureTopics = [],
  selectedTopic,
  onSelectTopic,
}) => {
  // ============================================================================
  // Render Regulatory Sidebar
  // ============================================================================

  const renderRegulatorySidebar = () => (
    <>
      {/* All Requirements */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">View</h4>
        <button
          className={`framework-sidebar__item ${selectedFramework === null ? 'framework-sidebar__item--active' : ''}`}
          onClick={() => onSelectFramework(null)}
        >
          <span>All Requirements</span>
          <span className="framework-sidebar__item-count">
            {frameworks.reduce((sum, f) => sum + f.requirementCount, 0)}
          </span>
        </button>
      </div>

      {/* Frameworks */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">Frameworks</h4>
        {frameworks.length === 0 ? (
          <p className="framework-sidebar__empty">No frameworks loaded</p>
        ) : (
          frameworks.map(fw => (
            <button
              key={fw.framework}
              className={`framework-sidebar__item ${selectedFramework === fw.framework ? 'framework-sidebar__item--active' : ''}`}
              onClick={() => onSelectFramework(fw.framework)}
              title={fw.fullName}
            >
              <div className="framework-sidebar__item-content">
                <span className="framework-sidebar__item-abbrev">{fw.framework}</span>
                <span className="framework-sidebar__item-name">{fw.fullName}</span>
              </div>
              <span className="framework-sidebar__item-count">{fw.citationCount}</span>
            </button>
          ))
        )}
      </div>

      {/* Categories */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">Categories</h4>
        <button
          className={`framework-sidebar__item ${selectedFramework === 'alcoa' ? 'framework-sidebar__item--active' : ''}`}
          onClick={() => onSelectFramework('alcoa')}
        >
          <span>ALCOA+ Data Integrity</span>
          <span className="framework-sidebar__item-badge framework-sidebar__item-badge--alcoa">9</span>
        </button>
        <button
          className={`framework-sidebar__item ${selectedFramework === 'fda_credibility' ? 'framework-sidebar__item--active' : ''}`}
          onClick={() => onSelectFramework('fda_credibility')}
        >
          <span>FDA AI/ML Credibility</span>
          <span className="framework-sidebar__item-badge framework-sidebar__item-badge--fda">7</span>
        </button>
      </div>
    </>
  );

  // ============================================================================
  // Render Literature Sidebar
  // ============================================================================

  const renderLiteratureSidebar = () => (
    <>
      {/* All Literature */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">View</h4>
        <button
          className={`framework-sidebar__item ${selectedTopic === null ? 'framework-sidebar__item--active' : ''}`}
          onClick={() => onSelectTopic?.(null)}
        >
          <span>All Citations</span>
          <span className="framework-sidebar__item-count">
            {literatureTopics.reduce((sum, t) => sum + t.citationCount, 0)}
          </span>
        </button>
      </div>

      {/* Topics */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">Topics</h4>
        {literatureTopics.length === 0 ? (
          <p className="framework-sidebar__empty">No topics available</p>
        ) : (
          literatureTopics.map(topic => (
            <button
              key={topic.id}
              className={`framework-sidebar__item ${selectedTopic === topic.id ? 'framework-sidebar__item--active' : ''}`}
              onClick={() => onSelectTopic?.(topic.id)}
              title={topic.description}
            >
              <span>{topic.name}</span>
              <span className="framework-sidebar__item-count">{topic.citationCount}</span>
            </button>
          ))
        )}
      </div>

      {/* Sources */}
      <div className="framework-sidebar__section">
        <h4 className="framework-sidebar__section-title">Sources</h4>
        <div className="framework-sidebar__source-item">
          <span className="framework-sidebar__source-icon">📚</span>
          <div className="framework-sidebar__source-content">
            <span className="framework-sidebar__source-name">PubMed</span>
            <span className="framework-sidebar__source-desc">NCBI Literature Database</span>
          </div>
        </div>
        <div className="framework-sidebar__source-item">
          <span className="framework-sidebar__source-icon">✓</span>
          <div className="framework-sidebar__source-content">
            <span className="framework-sidebar__source-name">Curated Library</span>
            <span className="framework-sidebar__source-desc">Expert-selected citations</span>
          </div>
        </div>
      </div>
    </>
  );

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <aside className="framework-sidebar" role="navigation" aria-label="Evidence navigation">
      {activeTab === 'regulatory' ? renderRegulatorySidebar() : renderLiteratureSidebar()}
    </aside>
  );
};

export default FrameworkSidebar;
